import {EditorSelection} from '@codemirror/state'
import type {Command, EditorView} from '@codemirror/view'

import {MarkdownLine} from '../../../language-tools/markdown/markdown-line'
import {escapeMarkdownContinuation} from '../../extensions/markdown-continuation'
import {getMarkdownTree} from '../../extensions/markdown-parser'

/**
 * Dispatch an undoable transaction to split the current blockquote apart, if the caret is on a blank line inside
 * of a blockquote of any depth. This operation only applies for a single caret with no text selected.
 */
export const splitBlockquoteIfBlankLine: Command = (view: EditorView) => {
  if (view.state.selection.ranges.length !== 1 || !view.state.selection.main.empty) return false

  const source = view.state.doc.toString()
  const caretPosition = view.state.selection.main.anchor
  const tree = getMarkdownTree(view.state)

  const line = MarkdownLine.atOffset(tree, source, caretPosition)
  if (!line?.isBlank) return false

  const lastPrefix = line.lastPrefix
  if (lastPrefix?.tokenType !== 'blockQuotePrefix') return false

  // Only split the blockquote if the caret is after the last prefix
  if (!line.isOffsetAfterPrefixes(caretPosition)) return false

  // Leave unchanged any prefixes on this line other than the last one
  const changeFrom = lastPrefix.position?.start.offset ?? line.start

  let insert = ''
  let newSelection = changeFrom

  // If the previous line is not blank, insert a blank line before this line to break the lazy continuation
  if (line.previous()?.isBlank === false) {
    insert += '\n'
    newSelection += 1
  }

  // If the next line is not blank, insert a blank line after this line to make things look nice
  if (line.next()?.isBlank === false) {
    insert += '\n'
  }

  view.dispatch({
    changes: [
      {
        from: changeFrom,
        to: line.end,
        insert,
      },
    ],
    // Update the selection to fall on the middle line
    selection: EditorSelection.single(newSelection),
    annotations: [escapeMarkdownContinuation.of('escapeBlockquote')],
    userEvent: 'input.type',
    scrollIntoView: true,
  })
  return true // handled
}
